import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { LocalCliError } from "./errors.js";

/**
 * Resolve the Key Vault URI from (in order): explicit override, AZURE_KEYVAULT_URI,
 * KEYVAULT_URI. Throws LocalCliError(KV_URI_NOT_FOUND) when nothing resolves.
 *
 * Lifted from the four 4×-duplicated copies in apps/cli/src/commands/brain/*.ts.
 * The (env, override) shape is the canonical one; check-app.ts
 * is normalized to it.
 */
export function discoverKvUri(env: Record<string, string | undefined>, override?: string): string {
  if (override) return override;
  const v = env.AZURE_KEYVAULT_URI ?? env.KEYVAULT_URI;
  if (v) return v;
  throw new LocalCliError({
    code: "KV_URI_NOT_FOUND",
    message: "Could not resolve Key Vault URI.",
    hint: "Pass --kv-uri or set AZURE_KEYVAULT_URI / KEYVAULT_URI.",
  });
}

/**
 * Read the absolute path of the m8t repo checkout from the
 * ~/.m8t/repo-root marker (written by guides/install/m8t.md), or `null`
 * when the marker is absent/empty. The non-throwing sibling of
 * `resolveRepoRoot` — used where the marker's absence is a legitimate fork
 * rather than an error (e.g. the updater job resolves baked content from
 * `/opt/m8t/repo` via this marker, but a founder/dev run has no marker and
 * falls back to fetching release content from the channel).
 */
export function tryResolveRepoRoot(): string | null {
  const marker = path.join(os.homedir(), ".m8t", "repo-root");
  if (!fs.existsSync(marker)) return null;
  const root = fs.readFileSync(marker, "utf8").trim();
  return root.length > 0 ? root : null;
}

/**
 * The directories that only a platform checkout has.
 *
 * `personas/` holds agent definitions; `deploy/` holds the bicep. Neither is in the agent
 * repository a founder now clones. Callers name the subset they actually read — see
 * `assertPlatformCheckout` — so this is the vocabulary, not a required set.
 */
export const PLATFORM_ROOTS = ["personas", "deploy"] as const;

/**
 * Refuse, by name, when the marker points at a checkout that cannot serve this
 * operation.
 *
 * The install runbook moved to the public agent repository, so `~/.m8t/repo-root` now
 * names THAT clone for anyone who installed from the published paste — a tree with
 * `agent/` and no `personas/` or `deploy/`. This is a shape the CLI had never been
 * handed before.
 *
 * The check is STRUCTURAL, up front, rather than letting each caller discover a missing
 * file: "fails confusingly" was the old worst case, but a command that globs `personas/`
 * and finds nothing would report success having done nothing at all, and a founder would
 * believe an agent was deployed. Loud refusal is the only acceptable outcome.
 *
 * The capability itself belongs to the CLI rewrite, which is scoped to assume no
 * platform-repo access. Until then this states the truth and names the path that works.
 */
export function assertPlatformCheckout(
  repoRoot: string,
  operation: string,
  needs: readonly string[] = ["personas"],
  overrides: {
    /**
     * Where `repoRoot` came from, when it is not the marker file. `bootstrap ui` takes
     * `--repo-root` and falls back to the working directory, and a message naming
     * `~/.m8t/repo-root` there sends a founder to edit a file that had nothing to do
     * with it.
     */
    source?: string;
    /**
     * The remedy, when the default does not fit. "your installation still updates
     * itself" is the right answer for `brain link` — it says the capability you were
     * reaching for is maintainer-side and nothing you depend on is affected. For the
     * onboarding chat it answers a question nobody asked, and says nothing about where
     * the founder actually meets their agent.
     */
    hint?: string;
  } = {},
): void {
  // What THIS operation reads, not the union of what any operation might: demanding
  // `deploy/` from a command that only renders a persona would refuse a checkout that
  // can serve it perfectly well, and a check that refuses correct input is one people
  // route around. `personas/` is the default because it is the directory every platform
  // checkout has and the agent repository does not.
  const missing = needs.filter((d) => !fs.existsSync(path.join(repoRoot, d)));
  if (missing.length === 0) return;
  const isAgentRepo = fs.existsSync(path.join(repoRoot, "agent", "persona.md"));
  throw new LocalCliError({
    code: "PLATFORM_CHECKOUT_REQUIRED",
    message:
      `${operation} needs a platform checkout, but ${overrides.source ?? "~/.m8t/repo-root"} ` +
      `points at '${repoRoot}', which has no ${missing.join("/ or ")}/.` +
      (isAgentRepo ? " That path is the agent repository — it carries the install runbook, not the platform." : ""),
    hint:
      overrides.hint ??
      "This operation reads platform content that is not published today, so it is " +
      "maintainer-side for now. Your installation still updates itself: the auto-update " +
      "rail applies published releases without needing any checkout.",
  });
}

/**
 * `resolveRepoRoot`, for the operations that read PLATFORM content out of it.
 *
 * Every caller that builds `<root>/personas/…` or `<root>/deploy/…` must come through
 * here rather than the bare resolver, so a checkout that cannot serve them refuses once,
 * by name, instead of each site discovering its own missing file. `brain-paths.test.ts`
 * holds the census of which callers those are.
 */
export function resolvePlatformRepoRoot(operation: string, needs?: readonly string[]): string {
  const root = resolveRepoRoot();
  // `needs` is forwarded so a caller declares the directory IT reads: seeding a brain
  // reads `brain-seeds/`, creating one reads `brain-template/`, and asserting `personas/`
  // for either would be checking a different claim than the one that matters.
  assertPlatformCheckout(root, operation, needs);
  return root;
}

/**
 * Read the absolute path of the m8t repo checkout from the
 * ~/.m8t/repo-root marker (written by the install runbook's local-tooling step). Throws
 * when the marker is missing — that means m8t was never installed.
 */
export function resolveRepoRoot(): string {
  const root = tryResolveRepoRoot();
  if (root === null) {
    throw new LocalCliError({
      code: "M8T_REPO_ROOT_MISSING",
      message: "~/.m8t/repo-root marker missing — m8t install never completed.",
    });
  }
  return root;
}
